import React, { useEffect, useState } from "react";
import { Tabs } from "antd";
import ConditionalRender from "../../../reusable-components/ConditionalRender";
import {
  getTabNameWithCount,
  processingReviewTabs,
  ticketProcessingReviewConstants,
} from "./constant";
import DataInput from "../DataInput";
import ApprovedTickets from "../ApprovedTickets";
import ArchivedTickets from "../ArchivedTickets";
import TicketProcessingServices from "../../../services/ticketProcessing.service";
import { useHistory, useLocation } from "react-router-dom";
import moment from "moment";
import { TabDataTypes } from "../../../enums/ViewListingTypes";
import RolesServices from "../../../services/roles.services";
import { useTicketProcessingFilterContext } from "../../../context/TicketProcessingFilterContext";
import styles from "./TicketProcessingReview.module.scss";
import { UserRoleActions } from "../../../enums/UserRolesEnum";
import TNLoader from "../../../reusable-components/TNLoader";

const TicketProcessingReview = () => {
  const { TabPane } = Tabs;
  const [currentTab, setCurrentTab] = useState("1");
  const location = useLocation();
  const history = useHistory();
  const { APPROVED, ARCHIVED, DATA_INPUT, MANAGER_REVIEW } =
    processingReviewTabs;
  const { processingFilters } = useTicketProcessingFilterContext();
  const { FetchTicketProcessingSectionCountMeta } = TicketProcessingServices();
  const { data, refetch, isPending } = FetchTicketProcessingSectionCountMeta({
    startDate: location?.state?.startDate,
    endDate: location?.state?.endDate,
    companyId: processingFilters?.customerName,
    jobId: processingFilters?.jobName,
    truckId: processingFilters?.truckNumber,
  });

  const userDetails = localStorage.getItem("userDetails");
  const userDetailsJson = JSON.parse(userDetails);
  const userRoleInfo = userDetailsJson?.["userRoleInfo"];
  const userRoleProfileInfo = userDetailsJson?.["userProfile"];

  const { FetchUserRoleDetails } = RolesServices();
  const { data: roleDetails } = FetchUserRoleDetails(userRoleInfo);

  const onlyDataInputTabAccess = !!userRoleProfileInfo
    ? roleDetails?.[0]?.otherData?.[4]?.multiPannel?.[1]?.selectedOptions?.includes(
        UserRoleActions.DATA_INPUT,
      )
    : true;

  const managerReviewTabAccess = !!userRoleProfileInfo
    ? roleDetails?.[0]?.otherData?.[4]?.multiPannel?.[1]?.selectedOptions?.includes(
        UserRoleActions.MANAGER_REVIEW,
      )
    : true;

  const archivedTabViewAccess = !!userRoleProfileInfo
    ? roleDetails?.[0]?.otherData?.[4]?.multiPannel?.[1]?.selectedOptions?.includes(
        UserRoleActions.ARCHIVED_VIEW,
      )
    : true;

  const archivedTabEditAccess = !!userRoleProfileInfo
    ? roleDetails?.[0]?.otherData?.[4]?.multiPannel?.[1]?.selectedOptions?.includes(
        UserRoleActions.ARCHIVED_EDIT,
      )
    : true;
  const invoiceManagementAccess = !!userRoleProfileInfo
    ? roleDetails?.[0]?.otherData?.[4]?.multiPannel?.[2]?.selectedOptions
        ?.length > 0
    : false;
  const handleTabChange = (key: string) => {
    setCurrentTab(key);
    switch (key) {
      case "1":
        history.replace({ ...history.location, hash: TabDataTypes.OPEN });
        break;
      case "2":
        history.replace({ ...history.location, hash: TabDataTypes.REVIEW });
        break;
      case "3":
        history.replace({ ...history.location, hash: TabDataTypes.APPROVED });
        break;
      case "4":
        history.replace({ ...history.location, hash: TabDataTypes.ARCHIVED });
        break;
    }
  };

  useEffect(() => {
    if (!!!location.hash) {
      history.replace({ ...location, hash: TabDataTypes.OPEN });
      setCurrentTab("1");
    } else {
      switch (location.hash.slice(1)) {
        case TabDataTypes.OPEN:
          setCurrentTab("1");
          break;
        case TabDataTypes.REVIEW:
          setCurrentTab("2");
          break;
        case TabDataTypes.APPROVED:
          setCurrentTab("3");
          break;
        case TabDataTypes.ARCHIVED:
          setCurrentTab("4");
          break;
      }
      refetch();
    }
  }, [location.pathname, location.hash]);

  useEffect(() => {
    refetch();
  }, [currentTab]);

  return (
    <>
    <TNLoader isLoading={isPending} />
    <div className={styles.tabViewWrapper}>
      <Tabs
        swipeable
        size={ticketProcessingReviewConstants.TAB_SIZE}
        destroyInactiveTabPane
        activeKey={currentTab ?? "1"}
        onChange={handleTabChange}
      >
        {(onlyDataInputTabAccess ||
          managerReviewTabAccess ||
          archivedTabViewAccess ||
          archivedTabEditAccess) && (
          <TabPane tab={getTabNameWithCount(DATA_INPUT, data?.open)} key={"1"}>
            <ConditionalRender
              visible={Number(data?.open) > 0}
              fallback={
                !isPending &&
                <div className={styles.emptyContainer}>
                  <p>{ticketProcessingReviewConstants.NO_DATA_TEXT}</p>
                </div>
              }
            >
              <DataInput
                key={TabDataTypes.OPEN}
                isManagerReview={currentTab === "2"}
              />
            </ConditionalRender>
          </TabPane>
        )}
        {(managerReviewTabAccess ||
          archivedTabViewAccess ||
          archivedTabEditAccess) && (
          <TabPane
            tab={getTabNameWithCount(MANAGER_REVIEW, data?.review)}
            key={"2"}
          >
            <ConditionalRender
              visible={Number(data?.review) > 0}
              fallback={
                !isPending &&
                <div className={styles.emptyContainer}>
                  <p>{ticketProcessingReviewConstants.NO_DATA_TEXT}</p>
                </div>
              }
            >
              <DataInput
                key={TabDataTypes.REVIEW}
                isManagerReview={currentTab === "2"}
              />
            </ConditionalRender>
          </TabPane>
        )}
        {(managerReviewTabAccess ||
          archivedTabViewAccess ||
          archivedTabEditAccess) && (
          <TabPane
            tab={getTabNameWithCount(APPROVED, data?.approved)}
            key={"3"}
          >
            <ConditionalRender
              visible={Number(data?.approved) > 0}
              fallback={
                !isPending &&
                <div className={styles.emptyContainer}>
                  <p>{ticketProcessingReviewConstants.NO_DATA_TEXT}</p>
                </div>
              }
            >
              <ApprovedTickets
                approveCount={Number(data?.approved)}
                hasInvoiceAccess={invoiceManagementAccess}
              />
            </ConditionalRender>
          </TabPane>
        )}
        {(archivedTabViewAccess || archivedTabEditAccess) && (
          <TabPane
            tab={getTabNameWithCount(ARCHIVED, data?.archived)}
            key={"4"}
          >
            <ConditionalRender
              visible={Number(data?.archived) > 0}
              fallback={
                !isPending &&
                <div className={styles.emptyContainer}>
                  <p>{ticketProcessingReviewConstants.NO_DATA_TEXT}</p>
                </div>
              }
            >
              <ArchivedTickets archivedTabEditAccess={archivedTabEditAccess} />
            </ConditionalRender>
          </TabPane>
        )}
      </Tabs>
    </div>
    </>
  );
};

export default TicketProcessingReview;
